import React, { useEffect, useState } from 'react'
import { AdminService } from '../../../services/admin.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleQuestion,
    faBuilding,
    faFileLines,
    faBook,
    faArrowTrendUp,
    faChartColumn,
    faChartPie,
} from '@fortawesome/free-solid-svg-icons'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Pie } from 'react-chartjs-2'

ChartJS.register(ArcElement, Tooltip, Legend)

const PIE_COLORS = ["#60a5fa", "#a78bfa", "#cbd5e1"]

const pieOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "bottom",
            labels: { usePointStyle: true, boxWidth: 8, padding: 14, color: "#6b7280", font: { size: 12 } },
        },
        tooltip: {
            backgroundColor: "#111827",
            padding: 12,
            cornerRadius: 8,
            callbacks: {
                // show both the raw value and its share of the slice total
                label: (c: any) => {
                    const value = Number(c.parsed) || 0
                    const total = (c.dataset?.data || []).reduce(
                        (sum: number, v: any) => sum + (Number(v) || 0),
                        0
                    )
                    const pct = total ? ((value / total) * 100).toFixed(1) : "0.0"
                    return ` ${c.label}: ${value.toLocaleString()} (${pct}%)`
                },
            },
        },
    },
}

export default function CompanyStats() {

    const [stats, setStats] = useState<any>()

    const getStats = async () => {
        await AdminService.getStats().then((res: any) => {
            setStats(res.data)
        }
        )
    }
    useEffect(() => {
        getStats()
    }, [])

    const kpis = [
        {
            label: "Total Questions",
            value: stats?.totalQuestions,
            icon: faCircleQuestion,
            foot: "Across all companies",
        },
        {
            label: "Total Companies",
            value: stats?.totalCompanies,
            icon: faBuilding,
            foot: "Registered organisations",
        },
        {
            label: "Total Question Papers",
            value: stats?.totalQuestionPapers,
            icon: faFileLines,
            foot: "Generated papers",
        },
        {
            label: "Total Subjects",
            value: stats?.totalSubjects,
            icon: faBook,
            foot: "Available subjects",
        },
    ]

    const sparkHeights = [38, 52, 44, 68, 58, 74, 64, 82, 70, 88, 78, 94]

    // Content breakdown for the Distribution pie. Total Questions (tens of
    // thousands) is intentionally excluded: mixing it with the small counts
    // collapses every other slice to a sliver. Questions already has its own
    // KPI card above. The pie compares same-order platform inventory counts.
    const distribution = [
        { name: "Question Papers", value: Number(stats?.totalQuestionPapers) || 0 },
        { name: "Subjects", value: Number(stats?.totalSubjects) || 0 },
        { name: "Companies", value: Number(stats?.totalCompanies) || 0 },
    ]
    const hasDistribution = distribution.some((d) => d.value > 0)
    const pieData = {
        labels: distribution.map((d) => d.name),
        datasets: [
            {
                data: distribution.map((d) => d.value),
                backgroundColor: PIE_COLORS,
                borderWidth: 0,
                hoverOffset: 6,
            },
        ],
    }

    return (
        <>
            <div className="ds-page-head">
                <div>
                    <h1 className="ds-page-title">Statistics</h1>
                    <p className="ds-page-sub">Overview of your platform activity</p>
                </div>
            </div>

            <div className="ds-kpi-grid">
                {kpis.map((kpi, index) => (
                    <div className="ds-kpi" key={index}>
                        <div className="ds-kpi-top">
                            <span className="ds-kpi-label">{kpi.label}</span>
                            <span className="ds-kpi-icon">
                                <FontAwesomeIcon icon={kpi.icon} />
                            </span>
                        </div>
                        <div className="ds-kpi-value">
                            {kpi.value !== undefined && kpi.value !== null
                                ? Number(kpi.value).toLocaleString()
                                : "—"}
                        </div>
                        <div className="ds-kpi-foot">{kpi.foot}</div>
                    </div>
                ))}
            </div>

            <div className="ds-stats-grid mt-3">
                <div className="ds-card ds-card-pad">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <div className="ds-cell-strong">Questions Overview</div>
                            <div className="ds-cell-label">Last 12 months</div>
                        </div>
                        <span className="ds-badge ds-badge--success">
                            <FontAwesomeIcon icon={faArrowTrendUp} /> Trending up
                        </span>
                    </div>
                    <div className="ds-spark">
                        {sparkHeights.map((h, i) => (
                            <span key={i} style={{ height: `${h}%` }} />
                        ))}
                    </div>
                    <div className="ds-chart-axis">
                        <span>Jan</span><span>Mar</span><span>May</span>
                        <span>Jul</span><span>Sep</span><span>Nov</span>
                    </div>
                </div>

                <div className="ds-card ds-card-pad">
                    <div className="ds-cell-strong mb-1">Distribution</div>
                    <div className="ds-cell-label mb-3">Content breakdown</div>
                    {hasDistribution ? (
                        <div style={{ position: "relative", width: "100%", minWidth: 0, height: 300 }}>
                            <Pie data={pieData} options={pieOptions} />
                        </div>
                    ) : (
                        <div className="ds-chart-placeholder">
                            <FontAwesomeIcon icon={faChartPie} style={{ fontSize: "1.6rem" }} />
                            <span>Chart visualization</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="ds-card ds-card-pad mt-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="ds-cell-strong">Recent Activity</div>
                    <FontAwesomeIcon icon={faChartColumn} className="ds-cell-muted" />
                </div>
                {[
                    { t: "New company registered", s: "Awaiting question upload" },
                    { t: "Question paper generated", s: "Subjects synced" },
                    { t: "Subject updated", s: "Topics added" },
                ].map((a, i) => (
                    <div className="ds-activity-item" key={i}>
                        <span className="ds-avatar ds-avatar--sm ds-avatar--neutral">
                            <FontAwesomeIcon icon={faChartColumn} />
                        </span>
                        <div>
                            <div className="ds-cell-value">{a.t}</div>
                            <div className="ds-cell-label">{a.s}</div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}
